import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { getAPI, postAPI, reinitialiseAPI } from 'store/apiReducer';
import PropTypes from 'prop-types';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import { FormProvider, useForm } from 'react-hook-form';
import { ButtonGroup, Button, Container, Row, Col, SegmentLoader } from '@si-armes/dsfr-react';
import {
    API_MODIFIER_ADRESSE_SECONDAIRE,
    API_POST_VERIFIER_BAN,
    API_RECUPERER_ADRESSE_SECONDAIRE,
    CONTEXT_API_MODIFIER_ADRESSE_SECONDAIRE,
    CONTEXT_API_RECUPERER_ADRESSE_SECONDAIRE,
    CONTEXT_POST_VERIFIER_BAN,
} from 'constants/apiUrls.constants';
import usagerClientReducer from 'store/usagerReducer';
import FormulaireAdresseSimple from 'components/form/formulaireAdresse/FormulaireAdresseSimple';
import ModalBan from 'components/modalBan/ModalBan';
import RoutesEcranGestion from '../gestion/routes';
import { getPath } from '../../../../utils/router';
import InfoChampsObligatoire from '../../../../components/form/infoObligatoire/InfoChampsObligatoire';

const ModificationAdresseSecondaire = ({
    getApi,
    postApi,
    reinitialiseApi,
    dataAdresse,
    isLoadingAdresse,
    dataBan,
    isLoadingBan,
    isLoadingModificationAdresse,
    erreurModificationAdresse,
}) => {
    const { idAdresse } = useParams();
    const history = useHistory();
    const location = useLocation();
    const uniquidPersonnePhysique = location.state?.uniquidPersonnePhysique;
    const hookForm = useForm({ mode: 'onChange' });
    const { handleSubmit, reset } = hookForm;
    const [dataToSubmit, setDataToSubmit] = useState(null);
    const [ouvrirModalBan, setOuvrirModalBan] = useState(false);

    useEffect(() => {
        const urlRecuperationAdresse = uniquidPersonnePhysique
            ? `${API_RECUPERER_ADRESSE_SECONDAIRE}${idAdresse}?uniquidPersonnePhysique=${uniquidPersonnePhysique}`
            : API_RECUPERER_ADRESSE_SECONDAIRE + idAdresse;
        getApi(
            usagerClientReducer,
            urlRecuperationAdresse,
            CONTEXT_API_RECUPERER_ADRESSE_SECONDAIRE,
        );
        return () => {
            reinitialiseApi(CONTEXT_API_RECUPERER_ADRESSE_SECONDAIRE);
            reinitialiseApi(CONTEXT_API_MODIFIER_ADRESSE_SECONDAIRE);
            reinitialiseApi(CONTEXT_POST_VERIFIER_BAN);
        };
    }, []);

    useEffect(() => {
        if (!isLoadingAdresse && dataAdresse) {
            reset({
                numeroDeVoie: dataAdresse.numeroDeVoie,
                extension: dataAdresse.extension,
                typeEtNomDeVoie: dataAdresse.libelleVoie,
                lieuDit: dataAdresse.lieuDit,
                codePostal: dataAdresse.codePostal,
                ville: dataAdresse.ville,
            });
        }
    }, [isLoadingAdresse, dataAdresse]);

    useEffect(() => {
        if (!erreurModificationAdresse && isLoadingModificationAdresse === false) {
            history.push(getPath(RoutesEcranGestion));
        }
    }, [isLoadingModificationAdresse, erreurModificationAdresse]);

    const verifierBan = (data) => {
        const adresseAverifier = {
            numeroDeVoie: data.numeroDeVoie,
            libelleVoie: data.typeEtNomDeVoie,
            codePostal: data.codePostal,
            ville: data.ville,
            extension: data.extension,
            lieuDit: data.lieuDit,
        };

        reinitialiseApi(CONTEXT_POST_VERIFIER_BAN);
        postApi(
            usagerClientReducer,
            API_POST_VERIFIER_BAN,
            adresseAverifier,
            CONTEXT_POST_VERIFIER_BAN,
        );
        setDataToSubmit(data);
        setOuvrirModalBan(true);
    };

    const onSubmit = async (dataToSend) => {
        const adresseAModifier = {
            ...dataToSend,
            idAdresse: dataAdresse.id,
            uniquidPersonnePhysique,
        };

        reinitialiseApi(CONTEXT_API_MODIFIER_ADRESSE_SECONDAIRE);
        postApi(
            usagerClientReducer,
            API_MODIFIER_ADRESSE_SECONDAIRE,
            adresseAModifier,
            CONTEXT_API_MODIFIER_ADRESSE_SECONDAIRE,
        );
    };

    const fermerModal = () => {
        setOuvrirModalBan(false);
        reinitialiseApi(CONTEXT_POST_VERIFIER_BAN);
    };

    const conserver = () => {
        fermerModal();
        onSubmit({ ...dataToSubmit, hasVerificationBan: false });
    };

    const remplacer = () => {
        fermerModal();
        onSubmit({
            ...dataToSubmit,
            numeroDeVoie: dataBan.numeroDeVoie,
            typeEtNomDeVoie: dataBan.libelleVoie,
            extension: dataBan.extension,
            lieuDit: dataBan.lieuDit,
            codePostal: dataBan.codePostal,
            ville: dataBan.ville,
            hasVerificationBan: true,
        });
    };

    const redirectToEcranGestion = () => {
        history.push(getPath(RoutesEcranGestion));
    };

    return (
        <Container>
            <Row>
                <Col col={12}>
                    <h1>Modification de l&apos;adresse secondaire</h1>
                </Col>
            </Row>
            <Row>
                <Col col={12}>
                    <InfoChampsObligatoire />
                </Col>
            </Row>
            <SegmentLoader isLoading={isLoadingAdresse || isLoadingModificationAdresse}>
                <FormProvider {...hookForm}>
                    <form onSubmit={handleSubmit(verifierBan)}>
                        <FormulaireAdresseSimple />
                        <Row className="margin-top-lg">
                            <Col col={12}>
                                <ButtonGroup align="right" inline>
                                    <Button
                                        name="etapePrecedente"
                                        variant="secondary"
                                        onClick={redirectToEcranGestion}
                                    >
                                        Retour
                                    </Button>
                                    <Button name="validerAdresse" variant="primary" type="submit">
                                        Valider
                                    </Button>
                                </ButtonGroup>
                            </Col>
                        </Row>
                    </form>
                </FormProvider>
            </SegmentLoader>
            {ouvrirModalBan && (
                <ModalBan
                    residence="secondaire"
                    fermerModal={fermerModal}
                    data={dataBan}
                    isLoading={isLoadingBan}
                    remplacer={remplacer}
                    conserver={conserver}
                />
            )}
        </Container>
    );
};

ModificationAdresseSecondaire.propTypes = {
    getApi: PropTypes.func.isRequired,
    postApi: PropTypes.func.isRequired,
    reinitialiseApi: PropTypes.func.isRequired,
    dataAdresse: PropTypes.shape({
        numeroDeVoie: PropTypes.string,
        id: PropTypes.number,
        extension: PropTypes.string,
        libelleVoie: PropTypes.string,
        lieuDit: PropTypes.string,
        codePostal: PropTypes.string,
        ville: PropTypes.string,
        pays: PropTypes.shape({
            libelle: PropTypes.string.isRequired,
        }).isRequired,
    }),
    isLoadingAdresse: PropTypes.bool,
    isLoadingBan: PropTypes.bool,
    dataBan: PropTypes.any,
    erreurModificationAdresse: PropTypes.bool,
    isLoadingModificationAdresse: PropTypes.bool,
};

export default connect(
    (state) => {
        const { data: dataAdresse, isLoading: isLoadingAdresse } =
            state.monCompte.recuperationAdresseSecondaire;
        const { erreur: erreurModificationAdresse, isLoading: isLoadingModificationAdresse } =
            state.monCompte.modificationAdresseSecondaire;
        const { isLoading: isLoadingBan, data: dataBan } = state.adresseVerificationBAN.verifier;
        return {
            isLoadingAdresse,
            dataAdresse,
            isLoadingBan,
            dataBan,
            isLoadingModificationAdresse,
            erreurModificationAdresse,
        };
    },
    {
        getApi: getAPI,
        postApi: postAPI,
        reinitialiseApi: reinitialiseAPI,
    },
)(ModificationAdresseSecondaire);
